import MarkerDetails from '@/components/MarkerDetails.vue';
import { MarkerDetailsModel } from '@/models/marker-details.model';
import { GeoJSONSourceRaw, Layer, LngLatLike, Marker } from 'mapbox-gl';
import { getCurvedLine } from '@/services/map.service';
import { getModule } from 'vuex-module-decorators';
import { MapStore } from '@/store/map';
import store from '@/store';
import bbox from '@turf/bbox';
import { lineString } from '@turf/helpers';
import { ExtraRegionalCities } from '@/models/table-fields/ExtraRegional.ts';

const SouthValleyCenter: LngLatLike = [5.270867, 48.978525];
const arrayOfMarkers: any = [];

const defaultLayers: string[] = [
  'poi-label',
  'settlement-subdivision-label',
  'settlement-minor-label',
  'settlement-major-label',
  'state-label',
  'country-label'
];

export const UpdateCityMarkers = () => {
  const mapStore = getModule(MapStore, store);

  const id = mapStore.selectedCityMarkerId;
  const travelType = mapStore.travelType;
  mapStore.cityMarkerComponents.forEach(marker => {
    marker.travelType = travelType;
    marker.selectedMarkerId = marker.id === id ? id : '';
  });
};

export const drawRoute = async () => {
  const mapStore = getModule(MapStore, store);

  const id = mapStore.selectedCityMarkerId;
  if (!id.length) return;

  const marker = mapStore.cityMarkerComponents.find(m => m.id === id);
  if (!marker) return;

  const position: number[] = [marker.lng, marker.lat];

  const source = getCurvedLine(SouthValleyCenter, position);

  if (mapStore.map.getSource('route')) {
    (mapStore.map.getSource('route') as mapboxgl.GeoJSONSource).setData(source);
  } else {
    const geojson: GeoJSONSourceRaw = {
      type: 'geojson',
      data: source
    };

    mapStore.addSource({ source: geojson, type: 'route' });

    const layer: Layer = {
      id: 'route',
      source: 'route',
      type: 'line',
      paint: {
        'line-color': '#00b6bc',
        'line-width': 3
      }
    };
    mapStore.addLayer({ layer, type: 'route' });
  }
};

export const AddCityMarkers = (response: ExtraRegionalCities[], travelType: string) => {
  const mapStore = getModule(MapStore, store);

  const markers: MarkerDetailsModel[] = [];
  response.forEach((i: any, index: number) => {
    const { lat, lng, car, transit } = i;
    const name = i.Villes;
    const airplane = i.plane;
    const id = `${index}`;
    const el = document.createElement('div');
    el.id = id;

    const markerDetails = new MarkerDetails({
      propsData: {
        id,
        name,
        car,
        airplane,
        transit,
        lat,
        lng,
        travelType,
        postTransit: i['post-transit'],
        preTransit: i['pre-transit'],
        postAirplane: i['post-airplane'],
        preAirplane: i['pre-airplane']
      }
    });
    markers.push(markerDetails);
    markerDetails.$mount();
    el.appendChild(markerDetails.$el);
    const marker = new Marker(el).setLngLat([lng, lat]);
    arrayOfMarkers.push(marker.getElement());

    marker.getElement().addEventListener('click', () => {
      arrayOfMarkers.forEach((mark: any) => {
        if (mark.id !== marker.getElement().id) {
          mark.style.zIndex = '1';
        } else {
          mark.style.zIndex = '1000';
        }
      });
      mapStore.setSelectedCityMarkerId(id);
      drawRoute();
      UpdateCityMarkers();
    });

    mapStore.addMarker({ marker, type: 'City' });
  });

  mapStore.setCityMarkerComponents(markers);
};

export const filterMarkersByTravelTime = (travelTime: number, travelType: string) => {
  const mapStore = getModule(MapStore, store);
  const lngLatBounds: any = [];
  mapStore.cityMarkers.forEach((marker: any) => {
    const id = marker.getElement().id;
    const cityMarkerComponent = mapStore.cityMarkerComponents.find(m => m.id === id);
    if (!cityMarkerComponent) return;
    const { car, transit, preTransit, postTransit, airplane, preAirplane, postAirplane } = cityMarkerComponent;
    const travelTypes: Record<string, number> = {
      car: car || 0,
      transit: (transit || 0) + (preTransit || 0) + (postTransit || 0),
      airplane: (airplane || 0) + (preAirplane || 0) + (postAirplane || 0)
    };
    if (travelTypes[travelType] && travelTypes[travelType] <= travelTime) {
      marker.getElement().style.visibility = 'visible';
      if (marker._lngLat) lngLatBounds.push([marker._lngLat.lng, marker._lngLat.lat]);
    } else {
      marker.getElement().style.visibility = 'hidden';
    }

    const selectedCityMarkerId = mapStore.selectedCityMarkerId;
    if (marker.getElement().style.visibility === 'hidden' && id === selectedCityMarkerId) {
      mapStore.removeLayer({ type: 'route' });
      mapStore.removeSource({ type: 'route' });
      cityMarkerComponent.selectedMarkerId = '';
      mapStore.setSelectedCityMarkerId('');
    }
  });
  // @ts-ignore
  if (lngLatBounds.length) mapStore.map.fitBounds(bbox(lineString(lngLatBounds)), { padding: 150 });
};

export const addSouthValleyMarker = () => {
  const mapStore = getModule(MapStore, store);
  const el = document.createElement('div');
  el.classList.add('marker-south-valley', 'background-color');
  const marker = new Marker(el).setLngLat(SouthValleyCenter);
  mapStore.addMarker({ marker, type: 'SouthValley' });
};

export const SetMapboxLabelsVisibility = (visibility: 'none' | 'visible') => {
  const mapStore = getModule(MapStore, store);
  defaultLayers.forEach(layer => {
    mapStore.map.setLayoutProperty(layer, 'visibility', visibility);
  });
};
